import type { CSSProperties } from 'react'
import type { IConfiguratorRenders } from '@/context/ProductDataStore'

// Share of the frame the headphones should fill once zoomed (the old images fill ~96%).
const FILL = 0.96

/**
 * The image to show for one part, and the image to hit-test clicks against.
 * Render layers come from S3 without CORS headers, so clicks use the backend's
 * click mask (a data URL) instead of reading the layer's pixels.
 */
export function layerImages(
  renders: IConfiguratorRenders | undefined,
  variantId: string,
  position: string | null,
  materialId: string,
): { src: string; hitSrc: string } {
  const url = renders?.layers[`${variantId}|${position || ''}|${materialId}`]
  const mask = renders?.masks[`${variantId}|${position || ''}`]
  if (url && mask) return { src: url, hitSrc: mask }
  const legacy = `/assets/headphones/front_view/${variantId}${position ? `_${position}` : ''}${materialId ? `_${materialId}` : ''}.webp`
  return { src: legacy, hitSrc: legacy }
}

/** Renders show the whole camera frame; zoom so the headphones fill it like the old images did. */
export function framingStyle(renders: Pick<IConfiguratorRenders, 'bounds'> | undefined): CSSProperties | undefined {
  const bounds = renders?.bounds
  if (!bounds) return undefined
  const [x0, y0, x1, y1] = bounds
  const scale = Math.max(1, Math.min(FILL / (x1 - x0), FILL / (y1 - y0)))
  const shift = (low: number, high: number) => `${((0.5 - (low + high) / 2) * scale * 100).toFixed(2)}%`
  return { scale: String(scale.toFixed(3)), translate: `${shift(x0, x1)} ${shift(y0, y1)}` }
}

type MaterialInfo = { materialId: string; hex_code?: string }

/** Hue (0–360), chroma and lightness (0–1) of a #rrggbb colour; null if it isn't one. */
function hueChromaLightness(hex: string | undefined): [number, number, number] | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex || '')
  if (!match) return null
  const [r, g, b] = [0, 2, 4].map(i => parseInt(match[1].slice(i, i + 2), 16) / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const lightness = (max + min) / 2
  const delta = max - min
  if (!delta) return [0, 0, lightness]
  const hue = max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4
  return [(hue * 60 + 360) % 360, delta, lightness]
}

// Below this chroma a colour reads as white/grey/black rather than a hue. (HSL saturation
// isn't used: it calls near-whites like Pearl #FAF6FF highly saturated.)
const NEUTRAL = 0.11

/**
 * Colours around the colour wheel (red → orange → yellow → green → blue → purple → pink),
 * then neutrals from light to dark. Colours without a hex keep their order at the end.
 */
export function colourWheelOrder(materialIds: string[], info: MaterialInfo[]): string[] {
  const byId = new Map(info.map(m => [m.materialId, m]))
  const key = (id: string): [number, number] => {
    const colour = hueChromaLightness(byId.get(id)?.hex_code)
    if (!colour) return [3, 0]
    const [hue, chroma, lightness] = colour
    if (chroma < NEUTRAL) return [2, 1 - lightness]
    // Crimson just short of 360° is red: start the wheel with it rather than after the pinks.
    return [1, hue < 345 ? hue : hue - 360]
  }
  return materialIds
    .map((id, i) => ({ id, i, k: key(id) }))
    .sort((a, b) => a.k[0] - b.k[0] || a.k[1] - b.k[1] || a.i - b.i)
    .map(item => item.id)
}

/** The colours a customer can pick for a part: offered and known to the shop, around the colour wheel. */
export function selectableMaterials(materialOptions: string[] | undefined, info: MaterialInfo[]): string[] {
  const known = new Set(info.map(m => m.materialId))
  return colourWheelOrder((materialOptions || []).filter(id => known.has(id)), info)
}

export interface EditablePart {
  key: string
  name: string
  position: string | null
  colours: string[] // material ids, around the colour wheel
  chosen: string
}

type EditorComponent = {
  name: string
  position: string | null
  chosenMaterial: { $oid: string }
  materialOptions?: string[]
  display?: Array<{ configuratorFrontOn: boolean }>
}

/** The parts a customer can change in the editor: shown in the configurator and with two or more colours. */
export function editableParts(components: EditorComponent[], info: MaterialInfo[]): EditablePart[] {
  return components.flatMap(component => {
    const colours = selectableMaterials(component.materialOptions, info)
    if (!component.display?.[0]?.configuratorFrontOn || colours.length < 2) return []
    return [{
      key: `${component.name}|${component.position ?? ''}`,
      name: component.name,
      position: component.position,
      colours,
      chosen: component.chosenMaterial?.$oid || '',
    }]
  })
}

/** A random colour for every editable part, never repeating the colour it already has. */
export function surpriseColours(parts: EditablePart[], random: () => number = Math.random) {
  return parts.map(part => {
    const others = part.colours.filter(id => id !== part.chosen)
    return { name: part.name, position: part.position, materialId: others[Math.floor(random() * others.length)] }
  })
}
